import React from "react";
import { useNavigate } from "react-router-dom";

import { Badge, BadgeProps, Button, Column, Headline, Paragraph, Table } from "@fronterahealth/frontera-ui-components";

import { ApiClinicListingVerificationStatusChoices, useGetClinicsQuery } from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";

interface Clinic {
  id: string;
  name: string;
  location: string;
  status: ApiClinicListingVerificationStatusChoices | null;
}

const statusAppearance: Record<ApiClinicListingVerificationStatusChoices, BadgeProps["appearance"]> = {
  [ApiClinicListingVerificationStatusChoices.Verified]: "success",
  [ApiClinicListingVerificationStatusChoices.Pending]: "warn",
  [ApiClinicListingVerificationStatusChoices.Unverified]: "neutral",
};

const statusLabel: Record<ApiClinicListingVerificationStatusChoices, string> = {
  [ApiClinicListingVerificationStatusChoices.Verified]: "Active",
  [ApiClinicListingVerificationStatusChoices.Pending]: "Pending Approval",
  [ApiClinicListingVerificationStatusChoices.Unverified]: "Unverified",
};

export const renderClinicStatus = (value: string | null) => {
  const status = value as ApiClinicListingVerificationStatusChoices | null;
  if (!status) return <span className="text-text-secondary">—</span>;
  return <Badge appearance={statusAppearance[status]} text={<b>{statusLabel[status]}</b>} />;
};

const tableColumns: Column<Clinic>[] = [
  {
    title: "Clinic Name",
    dataIndex: "name",
    key: "name",
    render: (value: string | null) => (
      <Paragraph colorType="primary" displayType="loud">
        {value}
      </Paragraph>
    ),
  },
  {
    title: "Location",
    dataIndex: "location",
    key: "location",
    render: (value: string | null) => <Paragraph colorType="secondary">{value}</Paragraph>,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: renderClinicStatus,
  },
];

export const Clinics: React.FC = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useGetClinicsQuery({}, { queryKey: ["getClinics"] });

  const clinics: Clinic[] =
    data?.clinics?.edges
      ?.flatMap((edge) => (edge?.node ? [edge.node] : []))
      .map((node) => {
        const location = node.location ? `${convertReadableString(node.location.city)}, ${node.location.state}` : "—";
        const status = node.cliniclistingSet?.edges?.[0]?.node?.verificationStatus ?? null;

        return {
          id: node.id,
          name: node.name,
          location,
          status,
        };
      }) ?? [];

  return (
    <div className="flex flex-col gap-4 mt-8">
      <div className="flex flex-row justify-between">
        <Headline>Clinics</Headline>
        <Button text="Add Clinic" onClick={() => navigate("add")} />
      </div>
      <Table
        cellClassName="bg-white"
        inputClassName="bg-white"
        data={clinics}
        columns={tableColumns}
        loading={isLoading}
        emptyMessage="No clinics yet"
        showPagination
        defaultItemsPerPage={5}
        onRowClick={(data) => navigate(`/clinics/${data.id}`)}
        actions={(data) => [
          data.status === ApiClinicListingVerificationStatusChoices.Verified && {
            label: "Edit",
            callback: (data) => navigate(`/clinics/${data.id}/edit`),
          },
        ]}
      />
    </div>
  );
};
